import { Component, Vue, Watch, Ref } from 'vue-property-decorator';
import { DataSetField } from '@/models/dataset/DataSetField';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { DataSetEventTypeEnums } from '@/enums/DataSetEventTypeEnums';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { QuickMenuData } from '@/models/general/QuickMenuData';
import { QuickMenuDataItem } from '@/models/general/QuickMenuDataItem';
import { DataSetStore } from '@/store/store.dataSet';
import { ConfigurationSectionTypeEnum } from '@/enums/ConfigurationSectionTypeEnums';
import { Configuration } from '@/models/configuration/Configuration';
import { ConfigurationField } from '@/models/configuration/Field';
import { ConfigurationSection } from '@/models/configuration/Section';
import { Helpers } from '@/helpers/Helpers';
import { DataSet, IDataSet } from '@/models/dataset/DataSet';
import { EventStore } from '@/store/store.event';
import { EventsService, SynchronizationService, DataSetService } from '@/services/index';
import { LoggerTypeEnum } from '@/enums/LoggerTypeEnums';
import { AppSettingsStore } from '@/store/store.appSettings';
import { EditorModeEnums } from '@/enums/EditorModeEnums';
import { DataSetSection } from '@/models/dataset/DataSetSection';
import { ApiIdentifers } from '@/config/index';
import { LoggerService } from '@/utils';
import { DataSetEditor } from '@/models/dataset/DataSetEditor';
import { AxDataService, ConfigurationService } from '@/services/index';

import { ISectionComponentMethods } from '@/components/edit/section/Section';

import HeaderBar from '@/components/general/headerbar/HeaderBar.vue';
import TitleButton from '@/components/general/titlebutton/TitleButton.vue';
import QuickSelectMenu from '@/components/general/quickselectmenu/QuickSelectMenu.vue';
import Section from '@/components/edit/section/Section.vue';
import { DataSetMyMillInformation } from '@/models/dataset/DataSetMyMillInformation';

@Component({
    components: {
        HeaderBar,
        Section,
        TitleButton,
        QuickSelectMenu
    }
})
export default class EditComponent extends Vue {
    public epindex: number = 0;
    public epIndizes: number[] = [0];
    public isReadonly: boolean = true;
    public saveButtonActive: boolean = true;

    public showMachinesButton: boolean = false;
    public showSamplesButton: boolean = false;

    public dialog: boolean = false;
    public dialogHeadline: string = '';
    public dialogText: string = '';
    public dialogCancel: string = '';
    public dialogOk: string = '';
    public dialogCheckbox: boolean = false;
    public dialogCheckboxShow: boolean = false;
    public dialogCheckboxText: string = '';
    public closeForm: boolean = false;

    public machinesQuickMenu: QuickMenuData = new QuickMenuData();
    public samplesQuickMenu: QuickMenuData = new QuickMenuData();

    public creator?: DataSetEditor = new DataSetEditor();
    public editor?: DataSetEditor = new DataSetEditor();
    public myMillImport?: DataSetMyMillInformation = new DataSetMyMillInformation();
    public showInfo: boolean = false;

    public quickCopyToast: boolean = false;
    public quickCopyToastTimeout: number = 3000;
    public quickCopyToastText: string = '';

    public useTableView?: boolean;

    public dataSetId?: string = '';

    public configurationSections: ConfigurationSection[] = [];
    private machinesConfiguration: Configuration = new Configuration();
    private samplesConfiguration: Configuration = new Configuration();
    private dataSet: IDataSet = new DataSet();
    private directDataSetId?: number;
    private dexiDataSetId?: number;
    private unsyncedDataSetId?: number;
    private goBackToDashboard: boolean = false;

    private cloneDataSetId?: string;

    // tslint:disable-next-line: no-empty
    public dialogCallbackOK = () => {};

    public async mounted() {
        this.unsyncedDataSetId = parseInt(this.$route.params.unsyncedId, 10) || undefined;
        this.directDataSetId = parseInt(this.$route.params.directId, 10) || undefined;
        this.dexiDataSetId = parseInt(this.$route.params.id, 10) || undefined;
        this.cloneDataSetId = this.$route.params.cloneId || undefined;
        this.useTableView = this.$route.params.table === 'false' ? false : this.$route.params.table === 'true' ? true : undefined;

        // Clear used sections in storage
        ConfigurationService.ClearUsedConfigurationSections();

        LoggerService.LogDebug(`id: ${this.dexiDataSetId}`);

        this.machinesConfiguration = new Configuration(this.getMachinesConfiguration);
        this.samplesConfiguration = new Configuration(this.getSamplesConfiguration);

        if (this.directDataSetId) {
            this.goBackToDashboard = true;
            this.dexiDataSetId = this.directDataSetId;
        }

        if (this.dexiDataSetId) {
            // TODO: edit existing dataSet
            AppSettingsStore.SetEditMode(EditorModeEnums.EDIT);
            const data = DataSetService.GetAllDataSets().find(ds => ds.id === this.dexiDataSetId);
            if (data !== undefined) {
                this.dataSetId = data.dataSetId;
                this.creator = data.created;
                this.editor = data.lastModified;
                this.myMillImport = data.myMillImport;
                await this.setDataFields(data);
            }
        } else if (this.unsyncedDataSetId) {
            // TODO: edit unsynced dataSet
            AppSettingsStore.SetEditMode(EditorModeEnums.EDIT_UNSYNCED);
            const data = EventStore.GetUnsyncedEvents.find(ur => ur.id === this.unsyncedDataSetId);
            if (data !== undefined) {
                await this.setUnsyncedDataFields(data);
            }
        } else if (this.cloneDataSetId) {
            // TODO: clone dataSet
            AppSettingsStore.SetEditMode(EditorModeEnums.CLONE);
            const cloneData = DataSetStore.GetCloneLineData;
            if (cloneData.length > 0) {
                const dataSet = new DataSet();
                cloneData.forEach(cd => {
                    dataSet.sections.push(new DataSetSection(cd));
                });
                await this.setDataFields(dataSet);
                await DataSetStore.ClearCloneLineData();
            }
        } else {
            // create new dataSet
            AppSettingsStore.SetEditMode(EditorModeEnums.CREATE);
        }

        this.configurationSections = this.getUsedConfigurationSections;
        this.generateMachinesQuickMenu();
        this.setUsedTemperatureFormat();
        this.setSelectedCustomer();
    }

    public beforeDestroy() {
        ConfigurationService.ClearUsedConfigurationSections();
        ConfigurationService.ClearUsedTemperatureFormat();
        AxDataService.SetSelectedCustomer();
    }

    get getUsedConfigurationSections() {
        return ConfigurationService.usedConfigurationSections;
    }

    get getMachinesConfiguration() {
        return ConfigurationService.machinesConfiguration;
    }

    get getSamplesConfiguration() {
        return ConfigurationService.samplesConfiguration;
    }

    @Watch('configurationSections', { deep: true })
    public onGetConfigurationSectionsChanged(newValue: ConfigurationSection[]) {
        // LoggerService.LogDebug(`onGetConfigurationSectionsChanged`);
        const machineOrder = newValue.find(x => x.identifier === ApiIdentifers.machineOrder);
        if (machineOrder) {
            const show = machineOrder.fields.some(field => field.type === FieldTypeEnum.CHECKBOX && field.valueBool && field.valueBool === true);
            if (show) {
                this.showMachinesButton = true;
                if (this.configurationSections.some(sec => sec.identifier === ApiIdentifers.sample) === false) {
                    this.showSamplesButton = true;
                } else {
                    this.showSamplesButton = false;
                }
            } else {
                this.showMachinesButton = false;
                this.showSamplesButton = false;
            }
        }
    }

    public onOpenRequest(sectionIndex: number) {
        const openIndex = this.epIndizes.findIndex(x => x === sectionIndex);

        LoggerService.LogDebug(`onOpenRequest: ${sectionIndex} | ${this.epindex}`);

        if (openIndex > -1) {
            if (this.checkOpenSection(sectionIndex) === true) {
                this.epIndizes.splice(openIndex, 1);
            }
        } else {
            this.epindex = sectionIndex;
            this.epIndizes.push(sectionIndex);
        }
    }

    public onClick(sectionIndex: number) {
        LoggerService.LogDebug(`Section ${this.configurationSections[sectionIndex].name} clicked: ${sectionIndex}`);
        this.onOpenRequest(sectionIndex);
    }

    public onSaveClicked(close: boolean) {
        this.closeForm = close;
        if (this.checkAllSectionComponents() === false) {
            return;
        } else if (this.checkAllSections() === true) {
            this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialog.save.title');
            this.dialogText = this.$vuetify.lang.t('$vuetify.edit.dialog.save.text');
            this.dialogCancel = this.$vuetify.lang.t('$vuetify.buttons.cancel');
            this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.save');
            this.dialogCheckboxText = this.$vuetify.lang.t('$vuetify.edit.dialog.save.pdf');
            this.dialogCheckboxShow = true;

            this.dialogCallbackOK = () => {
                this.dialog = false;
                this.dialogCheckboxShow = false;
                this.SaveSections(this.dialogCheckbox);
            };

            this.dialog = true;
        }
    }

    public onCancelClicked() {
        this.dialogHeadline = this.$vuetify.lang.t('$vuetify.edit.dialog.cancel.title');
        this.dialogText = this.$vuetify.lang.t('$vuetify.edit.dialog.cancel.text');
        this.dialogCancel = this.$vuetify.lang.t('$vuetify.buttons.cancel');
        this.dialogOk = this.$vuetify.lang.t('$vuetify.buttons.ok');

        this.dialogCallbackOK = () => {
            this.dialog = false;
            if (this.goBackToDashboard === true) {
                this.$router.replace({ name: 'dashboard' });
            } else {
                this.$router.back();
            }
        };

        this.dialog = true;
    }

    public onCopyDataSetId() {
        if (this.dataSetId) {
            const hiddenCopyField: HTMLInputElement = Helpers.getReference<HTMLInputElement>(this.$refs, 'hiddenCopyValue');
            if (hiddenCopyField !== undefined) {
                hiddenCopyField.value = this.dataSetId;
                hiddenCopyField.select();
                const copied: boolean = document.execCommand('copy');
                if (copied === true) {
                    LoggerService.LogDebug('Value copied to clipboard: ', hiddenCopyField.value);
                    this.quickCopyToastText = this.$vuetify.lang.t('$vuetify.edit.copyToClipboard', this.dataSetId);
                    this.quickCopyToast = true;
                } else {
                    LoggerService.LogWarn('Could not copy value to clipboard');
                }
            }
        }
    }

    public generateMachinesQuickMenu() {
        this.machinesQuickMenu.name = this.$vuetify.lang.t('$vuetify.buttons.machine');
        this.machinesQuickMenu.icon = 'mdi-plus';

        const machineOrder = this.configurationSections.find(x => x.identifier === ApiIdentifers.machineOrder);
        if (machineOrder) {
            const self = this;
            machineOrder.fields.forEach((field: ConfigurationField) => {
                // TODO: filter all already added machines
                if (field.name.toLocaleLowerCase() !== 'all') {
                    const item = new QuickMenuDataItem();
                    item.name = field.name;
                    item.identifier = field.identifier;
                    item.show = field.valueBool ? (field.valueBool as boolean) : false;
                    self.machinesQuickMenu.items.push(item);
                }
            });
        }

        const test = this.machinesQuickMenu.items.filter(mqmi => this.configurationSections.find(cs => cs.identifier === mqmi.identifier) !== undefined).forEach(qmitem => (qmitem.isAdded = true));

        this.showMachinesButton = this.machinesQuickMenu.items.some(x => !x.isAdded && x.show);
    }

    public getMachinesQuickMenu(): QuickMenuData {
        const machineOrder = this.configurationSections.find(x => x.identifier === ApiIdentifers.machineOrder);
        if (machineOrder) {
            machineOrder.fields.forEach((field: ConfigurationField) => {
                if (field.name.toLocaleLowerCase() !== 'all') {
                    const qmitem = this.machinesQuickMenu.items.find(item => item.identifier === field.identifier);
                    if (qmitem) {
                        qmitem.show = field.valueBool ? (field.valueBool as boolean) : false;
                    }
                }
            });
        }

        this.showMachinesButton = this.machinesQuickMenu.items.some(x => !x.isAdded && x.show);
        return this.machinesQuickMenu;
    }

    public onMachineValue(machineIdentifier: string) {
        LoggerService.LogDebug(`machineValue: ${machineIdentifier}`);
        const qmitem = this.machinesQuickMenu.items.find(item => item.identifier === machineIdentifier);
        if (qmitem) {
            qmitem.isAdded = true;
        }
        const machineSectionToAdd = this.addMachineRoom(machineIdentifier, this.configurationSections);
        if (machineSectionToAdd !== undefined) {
            ConfigurationService.SetUsedConfigurationSection(machineSectionToAdd);
        }

        const machineOrder = this.configurationSections.find(x => x.identifier === ApiIdentifers.machineOrder);
        if (machineOrder) {
            const mof = machineOrder.fields.find((field: ConfigurationField) => field.identifier === machineIdentifier);
            if (mof) {
                mof.disabled = true;
            }
        }
    }

    public onAddSample(data: any) {
        LoggerService.LogDebug(`onAddSample: `, data);
        if (this.configurationSections.some(sec => sec.identifier === ApiIdentifers.sample) === false) {
            const sampleSection = this.addSamplesSection();
            if (sampleSection !== undefined) {
                ConfigurationService.SetUsedConfigurationSection(sampleSection);
            }
        }
    }

    /**
     * Method to fetch translation with fallback
     * @param section ConfigurationSection
     */
    public getIdentifierText(section: ConfigurationSection): string {
        let ret: string = '';
        ret = this.$vuetify.lang.t(`$vuetify.identifiers.${section.identifier}`);
        // fallback to name string if no translation found
        if (ret === undefined || ret.length === 0 || ret === section.identifier || ret.includes('$vuetify.identifiers.')) {
            ret = section.name;
            LoggerService.LogDebug(`getIdentifierText fallback text for: ${section.identifier} --> ${ret}`);
        }
        return ret;
    }

    public getSetValuesCounter(section: ConfigurationSection): number {
        let setValueCount = 0;
        for (const field of section.fields) {
            setValueCount += field.getSetValuesCounter(section.identifier);
        }
        return setValueCount;
    }

    private getSectionRef(sectionRefName: string): ISectionComponentMethods & Section {
        return Helpers.getReference<ISectionComponentMethods & Section>(this.$refs, sectionRefName);
    }

    private async SaveSections(generatePdf: boolean) {
        // ✅ Sort
        this.configurationSections.sort((lhs, rhs) => lhs.position - rhs.position);

        // ✅ Keep
        this.sortSamples(this.configurationSections);

        this.ensureMaterialSubsectionFields(this.configurationSections);
        this.normalizeSelectFieldValues(this.configurationSections);
        this.clearEmptyOptionalFinenessValues(this.configurationSections);

        /**
         * ✅ ✅ FINAL FIX (MOST IMPORTANT)
         * Normalize EVERYTHING before save
         */
        this.configurationSections.forEach(section => {
            if (section.fields && section.fields.length > 0) {
                section.fields.forEach((field, index) => {
                    if (field.identifier.includes('__')) {
                        const baseIdentifier = section.identifier;
                        const baseName = field.name ? field.name.split('__')[0] : baseIdentifier;

                        field.identifier = `${baseIdentifier}__${index}`;
                        field.name = `${baseName}__${index}`;
                        field.position = index;
                    }
                });
            }
        });

        const editDataSet = new DataSet({
            sections: this.configurationSections
        });

        let eventType = DataSetEventTypeEnums.CREATE;
        let id: number | undefined;
        let previousDataSet: DataSet | undefined;

        if (this.dexiDataSetId == null && this.unsyncedDataSetId == null) {
            eventType = DataSetEventTypeEnums.CREATE;
        } else if (this.unsyncedDataSetId != null) {
            const oldUnsyncedDataSet = EventStore.unsyncedEvents.find(event => event.id === this.unsyncedDataSetId);

            if (oldUnsyncedDataSet !== undefined) {
                eventType = oldUnsyncedDataSet.event;
                id = oldUnsyncedDataSet.id;
                previousDataSet = oldUnsyncedDataSet.previousDataSet != null
                    ? DataSetService.CloneDataSet(oldUnsyncedDataSet.previousDataSet as DataSet)
                    : undefined;
            }
        } else if (this.dexiDataSetId != null) {
            eventType = DataSetEventTypeEnums.UPDATE;
            editDataSet.id = this.dexiDataSetId;
            editDataSet.dataSetId = this.dataSetId;
            editDataSet.unsyncedParts = true;

            const localDataSet = DataSetService.GetAllDataSets().find(ds => ds.id === this.dexiDataSetId);
            if (localDataSet !== undefined) {
                previousDataSet = DataSetService.CloneDataSet(localDataSet);
            }
        }

        try {
            const data: number = await EventsService.PutUnsyncedEvent(
                new DexieDataSetEvent({
                    dataSet: editDataSet,
                    previousDataSet,
                    event: eventType,
                    date: new Date().toISOString(),
                    id
                })
            );

            await EventStore.SetUnsyncedEvents();

            if (eventType === DataSetEventTypeEnums.UPDATE && editDataSet.id != null) {
                await DataSetService.ApplyLocalDataSetUpdate(editDataSet);
            }

            if (generatePdf) {
                this.$router.replace({
                    name: 'settingsReport',
                    params: { unsyncedEventId: data.toString() }
                });
            } else {
                if (SynchronizationService.IsRunnung()) {
                    SynchronizationService.SyncNow();
                }

                if (this.goBackToDashboard) {
                    this.$router.replace({ name: 'dashboard' });
                } else {
                    this.$router.back();
                }
            }
        } catch (error) {
            LoggerService.LogError(`Error on saving: `, error);
        }
    }

    private checkOpenSection(index?: number): boolean {
        const sectionRef = this.getSectionRef(`section_${index != null ? index : this.epindex}`);
        return sectionRef.validateSection();
    }

    private checkAllSectionComponents(): boolean {
        let allSectionsValid = true;
        const invalidSections: number[] = [];

        for (let index = 0; index < this.configurationSections.length; index++) {
            const sectionRef = this.getSectionRef(`section_${index}`);
            const valid = sectionRef != null ? sectionRef.validateSection() : this.configurationSections[index].isValidSection();
            if (valid === false) {
                this.epindex = index;
                invalidSections.push(index);
            }
        }

        if (invalidSections.length > 0) {
            this.epIndizes = invalidSections;
            allSectionsValid = false;
        }
        return allSectionsValid;
    }

    private checkAllSections(): boolean {
        let allSectionsValid = true;
        const invalidSections: number[] = [];

        const sectionCount = this.configurationSections.length;
        // tslint:disable-next-line: prefer-for-of
        for (let sectionIndex = 0; sectionIndex < sectionCount; sectionIndex++) {
            const section = this.configurationSections[sectionIndex];
            const ret: boolean = section.isValidSection();
            allSectionsValid = ret;
            if (allSectionsValid === false) {
                this.epindex = sectionIndex;
                invalidSections.push(sectionIndex);
            }
        }

        if (invalidSections.length > 0) {
            this.epIndizes = invalidSections;
            allSectionsValid = false;
        }

        return allSectionsValid;
    }

    private addMachineRoom(machineIdentifier: string, configurationSections: ConfigurationSection[]): ConfigurationSection | undefined {
        let sectionToAdd;
        if (configurationSections.find(section => section.identifier === machineIdentifier) === undefined) {
            const machineRoom = this.machinesConfiguration.sections.find(section => section.identifier === machineIdentifier);
            if (machineRoom) {
                sectionToAdd = new ConfigurationSection(machineRoom);
                sectionToAdd.type = ConfigurationSectionTypeEnum.MACHINE_ROOM;
                // just add 100 to position since machines also start at pos 1
                sectionToAdd.position += ApiIdentifers.machinesPositionMultiplier;
            } else {
                LoggerService.LogError(`Cant find machine room with identifier: ${machineIdentifier}`);
            }
        } else {
            LoggerService.LogInfo(`Machine room already added with identifier: ${machineIdentifier}`);
        }

        return sectionToAdd;
    }

    private addSamplesSection(): ConfigurationSection | undefined {
        let sectionToAdd;
        const sampleConfigSection = this.samplesConfiguration.sections.find(sec => sec.identifier === ApiIdentifers.sample);
        if (sampleConfigSection !== undefined) {
            sectionToAdd = new ConfigurationSection(sampleConfigSection);
            // just add 200 to position since samples also start at pos 1
            sectionToAdd.position += ApiIdentifers.samplesPositionMultiplier;
            ConfigurationService.generateActionSection(sectionToAdd);
        }
        return sectionToAdd;
    }

    private async setUnsyncedDataFields(inDexieDataSetEvent: DexieDataSetEvent) {
        this.setDataFields(inDexieDataSetEvent.dataSet as DataSet);
    }

    private async setDataFields(inDataSet: DataSet) {
        const usedDataSet = new DataSet(inDataSet);
        // first add machines
        const editConfigurationSections: ConfigurationSection[] = [];
        ConfigurationService.GetUsedConfigurationSections.forEach(sec => {
            editConfigurationSections.push(new ConfigurationSection(sec));
        });

        const editMachineSections = this.machinesConfiguration.sections.filter(mcs => usedDataSet.sections.find(y => y.identifier === mcs.identifier) !== undefined);
        for (const ems of editMachineSections) {
            const machineSectionToAdd = this.addMachineRoom(ems.identifier, editConfigurationSections);
            if (machineSectionToAdd !== undefined) {
                editConfigurationSections.push(machineSectionToAdd);
            }
        }

        const editSamples = usedDataSet.sections.find(sec => sec.identifier === ApiIdentifers.sample);
        if (editSamples !== undefined) {
            const sampleSection = this.addSamplesSection();
            if (sampleSection !== undefined) {
                editConfigurationSections.push(sampleSection);
            }
        }

        // set all vaules
        for (const inSection of usedDataSet.sections) {
            const editSection = editConfigurationSections.find(es => es.identifier === inSection.identifier);
            if (editSection !== undefined) {
                if (editSection.type === ConfigurationSectionTypeEnum.ACTION) {
                    this.loadActionSectionFields(editSection, inSection);
                } else {
                    for (const field of inSection.fields) {
                        const editField = editSection.fields.find(ef => ef.identifier === field.identifier);
                        if (editField !== undefined) {
                            this.setDataField(field, editField);
                        }
                    }
                }
            }
        }

        const machineOrder = editConfigurationSections.find(ecs => ecs.identifier === ApiIdentifers.machineOrder);
        if (machineOrder !== undefined) {
            machineOrder.fields.forEach(mof => {
                const process = editConfigurationSections.find(ecs => ecs.identifier === mof.identifier);
                if (process !== undefined) {
                    mof.valueBool = true;
                    mof.disabled = true;
                }
            });
        }

        await ConfigurationService.SetUsedConfigurationSections(editConfigurationSections);
    }

    private normalizeSelectFieldValues(sections: ConfigurationSection[]) {
        sections.forEach(section => {
            section.fields.forEach(field => this.normalizeSelectFieldValue(field));
        });
    }

    private normalizeSelectFieldValue(field: ConfigurationField) {
        if (field.fields) {
            field.fields.forEach(subField => this.normalizeSelectFieldValue(subField));
        }

        if (field.type === FieldTypeEnum.SELECT || field.type === FieldTypeEnum.UNIT) {
            if (field.valueNumber != null) {
                field.valueString = field.valueNumber.toString();
            }
        }
    }

    /**
     * Fineness value is optional. Clear NaN/empty leftovers so we never persist a fake 0
     * when only Material Component → Fineness Unit was auto-selected.
     */
    private clearEmptyOptionalFinenessValues(sections: ConfigurationSection[]) {
        sections.forEach(section => {
            section.fields.forEach(field => this.clearEmptyOptionalFinenessValue(field));
        });
    }

    private clearEmptyOptionalFinenessValue(field: ConfigurationField) {
        if (field.fields) {
            field.fields.forEach(subField => this.clearEmptyOptionalFinenessValue(subField));
        }

        if (field.identifier === ApiIdentifers.materialFineness) {
            if (field.valueNumber == null || isNaN(field.valueNumber)) {
                field.valueNumber = undefined;
                field.valueString = undefined;
            }
        }
    }

    /**
     * Every material row must include the full nested field set (component, blend, etc.)
     * so save/sync payloads are not sparse for 89a5a1f3__0 when only one value changed.
     */
    private ensureMaterialSubsectionFields(sections: ConfigurationSection[]) {
        const materialSection = sections.find(sec => sec.identifier === ApiIdentifers.material);
        if (materialSection === undefined || materialSection.fields.length === 0) {
            return;
        }

        const templateSection = ConfigurationService.GetConfiguration.sections.find(
            sec => sec.identifier === ApiIdentifers.material
        );
        const templateSubField = templateSection !== undefined && templateSection.fields.length > 0
            ? templateSection.fields[0]
            : materialSection.fields[0];

        if (templateSubField.fields === undefined) {
            return;
        }

        materialSection.fields.forEach(subField => {
            if (subField.fields === undefined) {
                subField.fields = [];
            }

            templateSubField.fields!.forEach(templateNested => {
                const existing = subField.fields!.find(f => f.identifier === templateNested.identifier);
                if (existing === undefined) {
                    const missingField = new ConfigurationField(templateNested);
                    missingField.resetFieldValue();
                    subField.fields!.push(missingField);
                }
            });

            subField.fields!.sort((lhs, rhs) => lhs.position - rhs.position);
        });
    }

    /**
     * Rebuilds material/sample/contact rows from the saved dataset only.
     * The default __0 template must not stay when the dataset has fewer rows (e.g. after delete).
     */
    private loadActionSectionFields(editSection: ConfigurationSection, inSection: DataSetSection) {
        const templateField = editSection.fields.length > 0 ? editSection.fields[0] : undefined;
        if (templateField == null) {
            return;
        }
        const rebuiltFields: ConfigurationField[] = [];
        const seenIdentifiers: Set<string> = new Set();

        const sortedFields = [...inSection.fields].sort((lhs, rhs) => lhs.position - rhs.position);
        for (const field of sortedFields) {
            if (seenIdentifiers.has(field.identifier)) {
                continue;
            }
            seenIdentifiers.add(field.identifier);

            const clonedSubSecFields = new ConfigurationField(templateField);
            if (clonedSubSecFields.fields) {
                clonedSubSecFields.fields.forEach((subField: ConfigurationField) => {
                    subField.resetFieldValue();
                });
            }

            const suffix = field.identifier.split('__')[1];
            clonedSubSecFields.position = suffix != null ? parseInt(suffix, 10) : rebuiltFields.length;
            clonedSubSecFields.identifier = field.identifier;
            const baseName = clonedSubSecFields.name
                ? clonedSubSecFields.name.split('__')[0]
                : field.identifier.split('__')[0];
            clonedSubSecFields.name = `${baseName}__${clonedSubSecFields.position}`;
            clonedSubSecFields.type = FieldTypeEnum.ACTION_SUBFIELD;
            this.setDataField(field, clonedSubSecFields);
            rebuiltFields.push(clonedSubSecFields);
        }

        if (rebuiltFields.length === 0 && templateField != null) {
            rebuiltFields.push(templateField);
        }

        editSection.fields = rebuiltFields;
    }

    private setDataField(inField: DataSetField, editField?: ConfigurationField) {
        if (editField !== undefined) {
            if ((editField.type === FieldTypeEnum.ACTION_SUBFIELD || editField.type === FieldTypeEnum.SUBFIELD) && inField.fields) {
                for (const subField of inField.fields) {
                    const subEditField = editField.fields!.find(esf => esf.identifier === subField.identifier);
                    if (subEditField !== undefined) {
                        this.setDataField(subField, subEditField);
                    }
                }
            } else {
                if (inField.valueBool !== undefined) {
                    editField.valueBool = inField.valueBool;
                }
                if (editField.type === FieldTypeEnum.SELECT || editField.type === FieldTypeEnum.UNIT) {
                    if (inField.valueNumber != null) {
                        editField.valueNumber = inField.valueNumber;
                        editField.valueString = inField.valueNumber.toString();
                    } else if (inField.valueString != null) {
                        editField.valueString = inField.valueString;
                        const parsedValue = parseInt(inField.valueString, 10);
                        if (!isNaN(parsedValue)) {
                            editField.valueNumber = parsedValue;
                        }
                    }
                } else {
                    if (inField.valueNumber != null) {
                        editField.valueNumber = inField.valueNumber;
                    }
                    if (inField.valueString != null) {
                        editField.valueString = inField.valueString;
                    }
                }
            }
        }
    }

    private sortSamples(inSections: ConfigurationSection[]) {
        const samples = inSections.find(sec => sec.identifier === ApiIdentifers.sample);

        if (samples !== undefined && samples.fields.length > 0) {
            const sortOrder = this.findSectionField(samples.fields[0], ApiIdentifers.sampleLaboratoryExtractionPoint);

            // ✅ Sorting remains same
            if (sortOrder !== undefined) {
                samples.fields.sort((lhs, rhs) => {
                    const lhsExPoint = this.findSectionField(lhs, ApiIdentifers.sampleLaboratoryExtractionPoint);
                    const rhsExPoint = this.findSectionField(rhs, ApiIdentifers.sampleLaboratoryExtractionPoint);

                    if (lhsExPoint && rhsExPoint) {
                        return sortOrder.values!.findIndex(item => item.value === lhsExPoint.valueNumber) - sortOrder.values!.findIndex(item => item.value === rhsExPoint.valueNumber);
                    }

                    return 0;
                });
            }

            /**
             * ✅ ✅ CRITICAL FIX
             * Rebuild identifier + name + position (DO NOT use replace)
             */
            const baseIdentifier = samples.identifier;
            const baseName = samples.name;

            for (let index = 0; index < samples.fields.length; index++) {
                const sample = samples.fields[index];

                // ✅ fix position
                sample.position = index;

                // ✅ FIX identifier properly
                sample.identifier = `${baseIdentifier}__${index}`;

                // ✅ FIX name also (very important)
                if (sample.name) {
                    sample.name = `${baseName}_${index}`;
                }
            }
        }
    }

    private findSectionField(inField: ConfigurationField, inIdentififer: string): ConfigurationField | undefined {
        let searchField: ConfigurationField | undefined;
        if (inField.identifier === inIdentififer) {
            searchField = inField;
        } else if (inField.fields !== undefined) {
            for (const subField of inField.fields) {
                const subSearchField = this.findSectionField(subField, inIdentififer);
                if (subSearchField !== undefined) {
                    searchField = subSearchField;
                    break;
                }
            }
        }
        return searchField;
    }

    private setUsedTemperatureFormat() {
        const secGeneral = this.configurationSections.find(sec => sec.identifier === ApiIdentifers.general);
        if (secGeneral != null) {
            const tempFormat = secGeneral.fields.find(field => field.identifier === ApiIdentifers.generalTemperatureFormat);
            if (tempFormat != null) {
                ConfigurationService.SetUsedTemperatureFormat(tempFormat.valueNumber || 0);
            }
        }
    }

    private setSelectedCustomer() {
        const customerData = this.configurationSections.find(sec => sec.identifier === ApiIdentifers.customer);
        if (customerData != null) {
            const customerName = this.findSectionField(customerData.fields[0], ApiIdentifers.customerName);
            if (customerName != null) {
                AxDataService.SetSelectedCustomer(customerName.valueString);
            }
        }
    }
}
